import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { JobsPerformanceTable, JobsFilter } from '../shared/JobsPerformanceTable'
import { RefreshCw } from 'lucide-react'

interface LiveCollectionMonitorProps {
  onSmartFilter?: (filter: JobsFilter) => void
  organizationId?: number
}

/**
 * Live Collection Monitor - Tab 1
 *
 * Shows the jobs performance table with hourly breakdown.
 * Clicking cells triggers smart filter to open Jobs tab with that selection.
 * Refresh button reloads the performance data.
 */
export function LiveCollectionMonitor({ onSmartFilter, organizationId }: LiveCollectionMonitorProps) {
  const [refreshKey, setRefreshKey] = useState(0)
  const [loading, setLoading] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  const handleRefresh = () => {
    setLoading(true)
    setRefreshKey(prev => prev + 1)
    setLastRefresh(new Date())
    setTimeout(() => setLoading(false), 500)
  }

  const handleFilterSelect = (filter: JobsFilter | null) => {
    if (filter && onSmartFilter) {
      onSmartFilter(filter)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Live Collection Monitor</CardTitle>
            <CardDescription>
              Last 24 hours performance by hour (UTC) - Updated {lastRefresh.toLocaleTimeString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' })} UTC
              {onSmartFilter && <span className="ml-2 text-xs">(Click cells to filter Jobs tab)</span>}
            </CardDescription>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 pb-6">
        <JobsPerformanceTable
          key={`${refreshKey}-${organizationId}`}
          onFilterSelect={onSmartFilter ? handleFilterSelect : undefined}
          organizationId={organizationId}
        />
      </CardContent>
    </Card>
  )
}

export default LiveCollectionMonitor
